import type { ReactNode } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { breakpoints } from '__app/spacing';
import { fontWeights } from '__app/typography';
import Button from '__components/Button';
import BaseModal, {
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '__components/Modals';

type ActionModalProps = {
  children: ReactNode;
  disableButton?: boolean;
  isFailed?: boolean;
  isMobile: boolean;
  rightButtonTitle?: string;
  show: boolean;
  title?: string;
  onHide(): void;
  onSubmit(): void;
};

const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  overflow-y: auto; // Removes unnecessary scrollbar, which allows things to size/align per designs
  overflow-x: hidden;

  > button,
  > span {
    padding: 0;
    margin: 0;
    margin-bottom: 15px;
    &:empty {
      margin-bottom: 0;
    }

    font-weight: ${fontWeights.black};
    letter-spacing: 0.5px;
    border-width: 1px;
    line-height: 35px;
    height: 35px;

    width: 141px;
    @media (min-width: ${breakpoints.lg}px) {
      width: 180px;
    }
  }
`;

const StyledModalFooter = styled(ModalFooter)`
  padding: 0 14px;
  .button-box {
    width: 100%;
    margin-bottom: 14px;
    display: flex;
    justify-content: flex-end;

    > button.btn {
      margin: 0;
    }
  }
`;

export function ActionModal({
  title = '',
  onHide,
  onSubmit,
  disableButton = false,
  children,
  rightButtonTitle,
  isFailed = false,
  ...props
}: ActionModalProps) {
  const { t } = useTranslation();
  return (
    <BaseModal size="medium" onHide={onHide} enforceFocus={false} {...props}>
      <ModalHeader onClickClose={onHide}>{title}</ModalHeader>
      <StyledModalBody>{children}</StyledModalBody>
      <StyledModalFooter showBorder={false}>
        <div className="button-box">
          <>
            <Button
              variant={isFailed ? 'default' : 'text'}
              color="blue"
              onClick={onHide}
            >
              {t('Close')}
            </Button>
            {isFailed ? null : (
              <Button onClick={onSubmit} disabled={disableButton}>
                {rightButtonTitle || t('Confirm')}
              </Button>
            )}
          </>
        </div>
      </StyledModalFooter>
    </BaseModal>
  );
}
